import React, { useMemo } from 'react';

import {
  GoalName,
  type Scalars,
} from '../../__generated-gql-types__/globalTypes';
import { CareerOutcomes } from '../outcomes/CareerOutcome';
import { SchoolOutcomes } from '../outcomes/SchoolOutcome';
import { TestOutcomes } from '../outcomes/TestOutcome';

import {
  type CoachGoalSpecificOutcomesCareerOutcomeSummaryFragment,
  type CoachGoalSpecificOutcomesCoachFragment,
  type CoachGoalSpecificOutcomesSchoolOutcomeSummaryFragment,
  type CoachGoalSpecificOutcomesTestOutcomeSummaryFragment,
} from './__generated-gql-types__/CoachGoalSpecificOutcomes.generated';

const MINIMUM_OUTCOMES = 2;

interface CoachGoalSpecificOutcomesProps {
  coach: CoachGoalSpecificOutcomesCoachFragment;
  goal: GoalName;
  categoryId?: Scalars['CategoryUrn']['output'];
  className?: string;
}

const CoachGoalSpecificOutcomes: React.FC<CoachGoalSpecificOutcomesProps> = ({
  coach,
  goal,
  categoryId,
  className = '',
}) => {
  const coachOutcomes = coach.outcomes;
  const coachName = coach.user.firstName;

  const { coachSchoolOutcomes, coachCareerOutcomes, coachTestOutcomes } =
    useMemo(() => {
      if (!coachOutcomes) {
        return {};
      }

      const outcomesObj: {
        coachSchoolOutcomes?: Possible<CoachGoalSpecificOutcomesSchoolOutcomeSummaryFragment>;
        coachCareerOutcomes?: Possible<CoachGoalSpecificOutcomesCareerOutcomeSummaryFragment>;
        coachTestOutcomes?: Possible<CoachGoalSpecificOutcomesTestOutcomeSummaryFragment>;
      } = {};

      coachOutcomes.map((outcome) => {
        if (outcome.__typename === 'SchoolOutcomeSummary') {
          outcomesObj.coachSchoolOutcomes = outcome;
        } else if (outcome.__typename === 'CareerOutcomeSummary') {
          outcomesObj.coachCareerOutcomes = {
            ...outcome,
            companies: outcome.companies.filter((s) => s.name != 'N/A'),
          };
        } else if (
          outcome.__typename === 'TestOutcomeSummary' &&
          outcome.category.id === categoryId
        ) {
          outcomesObj.coachTestOutcomes = outcome;
        }
      });

      return outcomesObj;
    }, [coachOutcomes, categoryId]);

  return (
    <>
      {goal === GoalName.SCHOOL &&
      coachSchoolOutcomes &&
      coachSchoolOutcomes.schools.length >= MINIMUM_OUTCOMES ? (
        <SchoolOutcomes
          className={className}
          outcomes={coachSchoolOutcomes}
          coachName={coachName}
        />
      ) : null}
      {goal === GoalName.CAREER &&
      coachCareerOutcomes &&
      coachCareerOutcomes.companies.length >= MINIMUM_OUTCOMES ? (
        <CareerOutcomes
          className={className}
          outcomes={coachCareerOutcomes}
          coachName={coachName}
        />
      ) : null}
      {goal === GoalName.TEST && coachTestOutcomes?.scoreRange ? (
        <TestOutcomes
          className={className}
          outcome={coachTestOutcomes}
          coachName={coachName}
        />
      ) : null}
    </>
  );
};

export default CoachGoalSpecificOutcomes;
